import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { RefreshCw, LogOut, Eye, MessageCircle, Users, Send, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_NAMES = {
  '/': 'Landing (chooser)',
  '/geet': 'Geet — Home',
  '/geet/about': 'Geet — About',
  '/geet/menu': 'Geet — Menu',
  '/geet/gallery': 'Geet — Gallery',
  '/geet/contact': 'Geet — Contact',
  '/ogeet': 'OGeet — Home',
  '/ogeet/moments': 'OGeet — Moments',
};
const prettyPage = (p) => PAGE_NAMES[p] || p || '/';
const ACTION_NAMES = {
  whatsapp_click: 'WhatsApp', directions: 'Directions', instagram: 'Instagram',
  menu_download: 'Menu PDF', reserve: 'Reserve',
};
const DEVICE_COLORS = { mobile: '#25D366', desktop: '#0ea5e9', tablet: '#f59e0b', unknown: '#a8a29e' };
const PIE = ['#0ea5e9', '#25D366', '#f59e0b', '#7c3aed', '#d946ef', '#a8a29e'];

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-stone-200 p-6 shadow-sm ${className}`}>{children}</div>
);

const StatCard = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <Card>
    <div data-testid={testid} className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}1a`, color: accent }}>
        <Icon size={20} />
      </div>
      <p className="text-sm font-medium text-stone-500">{label}</p>
    </div>
    <p className="text-4xl font-semibold text-stone-900">{value}</p>
    {sub != null && <p className="text-xs text-stone-400 mt-1">{sub}</p>}
  </Card>
);

const Delta = ({ now, prev }) => {
  const diff = now - prev;
  const pct = prev ? Math.round((diff / prev) * 100) : (now ? 100 : 0);
  const up = diff >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{up ? '+' : ''}{pct}%
    </span>
  );
};

export default function AnalyticsDashboard() {
  const [key, setKey] = useState(localStorage.getItem('geet_analytics_key') || '');
  const [authed, setAuthed] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const load = async (accessKey, d = days) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/analytics/detailed?days=${d}`, { headers: { 'X-Analytics-Key': accessKey } });
      setData(res.data);
      setAuthed(true);
      localStorage.setItem('geet_analytics_key', accessKey);
      axios.get(`${API}/analytics/geo?days=${d}`, { headers: { 'X-Analytics-Key': accessKey } })
        .then((g) => setGeo(g.data)).catch(() => setGeo(null));
    } catch (e) {
      if (e?.response?.status === 401) {
        setError('Invalid access key.');
        setAuthed(false);
        localStorage.removeItem('geet_analytics_key');
      } else {
        setError('Could not load analytics.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (key) load(key); /* eslint-disable-next-line */ }, []);

  const changeRange = (d) => { setDays(d); load(localStorage.getItem('geet_analytics_key'), d); };

  const sendReport = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/analytics/send-report`, {}, { headers: { 'X-Analytics-Key': localStorage.getItem('geet_analytics_key') } });
      if (res.data?.sent) alert('Weekly report emailed to markasurgeon@gmail.com ✅');
      else if (res.data?.reason === 'missing_api_key') alert('Email not configured — Resend API key missing on server.');
      else alert(`Could not send report: ${res.data?.reason || 'unknown'}`);
    } catch { alert('Could not send report.'); } finally { setSending(false); }
  };

  const logout = () => { localStorage.removeItem('geet_analytics_key'); setAuthed(false); setData(null); setKey(''); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-6">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-stone-900 mb-1">Analytics</h1>
          <p className="text-sm text-stone-500 mb-6">Enter the access key to view site stats.</p>
          <input
            data-testid="analytics-key-input" type="password" value={key}
            onChange={(e) => setKey(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(key)}
            placeholder="Access key"
            className="w-full border border-stone-300 rounded-lg px-4 py-3 mb-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          {error && <p data-testid="analytics-error" className="text-sm text-red-600 mb-3">{error}</p>}
          <button data-testid="analytics-login-btn" onClick={() => load(key)} disabled={loading || !key}
            className="w-full bg-stone-900 text-white rounded-lg py-3 font-medium hover:bg-stone-700 transition-colors disabled:opacity-50">
            {loading ? 'Checking…' : 'View Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};
  const wow = data?.week_over_week || { this_week: {}, last_week: {} };
  const vv = data?.views_by_venue || {};
  const conv = data?.conversion || {};
  const venueData = [
    { venue: 'Geet', Views: vv.geet || 0 },
    { venue: 'OGeet', Views: vv.ogeet || 0 },
    { venue: 'Landing', Views: vv.landing || 0 },
  ];
  const deviceData = Object.entries(data?.devices || {}).map(([name, value]) => ({ name, value }));
  const browserData = Object.entries(data?.browsers || {}).map(([name, value]) => ({ name, value }));
  const actionData = Object.entries(data?.actions_by_type || {}).map(([k, v]) => ({ action: ACTION_NAMES[k] || k, count: v }));
  const maxPageViews = Math.max(1, ...(data?.top_pages || []).map((p) => p.views));

  return (
    <div className="min-h-screen bg-stone-100 px-4 md:px-8 py-8" data-testid="analytics-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-stone-900">Geet · Analytics</h1>
            <p className="text-sm text-stone-500">{data?.range_label}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex bg-white border border-stone-300 rounded-lg overflow-hidden">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`range-${d}`} onClick={() => changeRange(d)}
                  className={`px-3 py-2 text-sm ${days === d ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>{d}d</button>
              ))}
            </div>
            <button data-testid="analytics-send-report-btn" onClick={sendReport} disabled={sending}
              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-lg px-3 py-2 text-sm hover:bg-[#1EBE57] disabled:opacity-50">
              <Send size={15} /> {sending ? 'Sending…' : 'Email report'}
            </button>
            <button data-testid="analytics-refresh-btn" onClick={() => load(localStorage.getItem('geet_analytics_key'))}
              className="inline-flex items-center gap-2 bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button data-testid="analytics-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 px-2">
              <LogOut size={15} />
            </button>
          </div>
        </div>

        {/* stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard testid="stat-views" label="Page Views" value={t.page_views ?? 0}
            sub={<Delta now={wow.this_week.page_views || 0} prev={wow.last_week.page_views || 0} />} icon={Eye} accent="#0ea5e9" />
          <StatCard testid="stat-unique" label="Unique Visitors" value={t.unique_visitors ?? 0}
            sub={`${t.returning_visitors ?? 0} returning`} icon={Users} accent="#7c3aed" />
          <StatCard testid="stat-whatsapp" label="WhatsApp Taps" value={t.whatsapp_clicks ?? 0}
            sub={<Delta now={wow.this_week.whatsapp_clicks || 0} prev={wow.last_week.whatsapp_clicks || 0} />} icon={MessageCircle} accent="#25D366" />
          <StatCard testid="stat-conversion" label="Booking Conversion" value={`${Math.max(conv.geet || 0, conv.ogeet || 0)}%`}
            sub={`Geet ${conv.geet || 0}% · OGeet ${conv.ogeet || 0}%`} icon={TrendingUp} accent="#d946ef" />
        </div>

        {/* Top pages — priority */}
        <Card className="mb-4">
          <h2 className="text-lg font-medium text-stone-800 mb-4">Most viewed pages</h2>
          <div className="space-y-2" data-testid="top-pages">
            {(data?.top_pages || []).length === 0 && <p className="text-sm text-stone-400">No page views in this period yet.</p>}
            {(data?.top_pages || []).map((p) => (
              <div key={p.path} className="flex items-center gap-3">
                <div className="w-40 md:w-56 shrink-0 text-sm text-stone-700 truncate">{prettyPage(p.path)}</div>
                <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full flex items-center justify-end pr-2 text-[11px] text-white font-medium"
                    style={{ width: `${Math.max(8, (p.views / maxPageViews) * 100)}%` }}>{p.views}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Daily trend</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="date" tickFormatter={(d) => (d || '').slice(5)} stroke="#78716c" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#78716c" fontSize={11} />
                <Tooltip /><Legend />
                <Bar dataKey="page_views" name="Views" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="whatsapp_clicks" name="WhatsApp" fill="#25D366" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Views by venue</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={venueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="venue" stroke="#78716c" fontSize={12} />
                <YAxis allowDecimals={false} stroke="#78716c" fontSize={11} />
                <Tooltip />
                <Bar dataKey="Views" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mb-4">
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Devices</h2>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={deviceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {deviceData.map((d, i) => <Cell key={i} fill={DEVICE_COLORS[d.name] || PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Browsers</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={browserData} layout="vertical">
                <XAxis type="number" allowDecimals={false} stroke="#78716c" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#78716c" fontSize={11} width={60} />
                <Tooltip />
                <Bar dataKey="value" name="Visits" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Button clicks</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={actionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="action" stroke="#78716c" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#78716c" fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" name="Clicks" fill="#25D366" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">By hour (UTC)</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data?.by_hour || []}>
                <XAxis dataKey="hour" stroke="#78716c" fontSize={10} />
                <YAxis allowDecimals={false} stroke="#78716c" fontSize={11} />
                <Tooltip />
                <Bar dataKey="events" name="Events" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">By weekday</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data?.by_weekday || []}>
                <XAxis dataKey="day" stroke="#78716c" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#78716c" fontSize={11} />
                <Tooltip />
                <Bar dataKey="events" name="Events" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Traffic sources</h2>
            <table className="w-full text-sm" data-testid="sources-table">
              <thead><tr className="text-stone-400 text-left"><th className="pb-2 font-medium">Source</th><th className="pb-2 font-medium text-right">Visits</th></tr></thead>
              <tbody>
                {(data?.sources || []).map((s) => (
                  <tr key={s.source} className="border-t border-stone-100"><td className="py-2 text-stone-700">{s.source}</td><td className="py-2 text-right text-stone-900 font-medium">{s.visits}</td></tr>
                ))}
                {(data?.sources || []).length === 0 && <tr><td className="py-2 text-stone-400">No data yet</td><td /></tr>}
              </tbody>
            </table>
          </Card>
          <Card>
            <h2 className="text-lg font-medium text-stone-800 mb-4">Top locations</h2>
            <table className="w-full text-sm" data-testid="geo-table">
              <thead><tr className="text-stone-400 text-left"><th className="pb-2 font-medium">Country</th><th className="pb-2 font-medium text-right">Visitors</th></tr></thead>
              <tbody>
                {!geo && <tr><td className="py-2 text-stone-400">Loading…</td><td /></tr>}
                {geo && (geo.countries || []).map((c) => (
                  <tr key={c.country} className="border-t border-stone-100"><td className="py-2 text-stone-700">{c.country}</td><td className="py-2 text-right text-stone-900 font-medium">{c.visitors}</td></tr>
                ))}
                {geo && (geo.countries || []).length === 0 && <tr><td className="py-2 text-stone-400">No data yet</td><td /></tr>}
              </tbody>
            </table>
          </Card>
        </div>

        {/* recent activity */}
        <Card>
          <h2 className="text-lg font-medium text-stone-800 mb-4">Recent activity</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-table">
              <thead><tr className="text-stone-400 text-left">
                <th className="pb-2 font-medium">Time</th><th className="pb-2 font-medium">Event</th>
                <th className="pb-2 font-medium">Page</th><th className="pb-2 font-medium">Device</th><th className="pb-2 font-medium">Source</th>
              </tr></thead>
              <tbody>
                {(data?.recent || []).map((r, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="py-2 text-stone-500 whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</td>
                    <td className="py-2 text-stone-700">{r.type === 'page_view' ? 'View' : (ACTION_NAMES[r.type] || r.type)}</td>
                    <td className="py-2 text-stone-600">{prettyPage(r.path)}</td>
                    <td className="py-2 text-stone-600">{r.device || '—'}</td>
                    <td className="py-2 text-stone-600">{r.source || '—'}</td>
                  </tr>
                ))}
                {(data?.recent || []).length === 0 && <tr><td className="py-2 text-stone-400" colSpan={5}>No activity yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
